// White list of allowed CSS properties for inline styles
// This avoid XSS attacks via CSS injection
const ALLOWED_STYLE_PROPERTIES = [
    // Layout
    "display",
    "position",
    "top",
    "right",
    "bottom",
    "left",
    "z-index",
    "float",
    "clear",
    "overflow",
    "overflow-x",
    "overflow-y",

    // Box model
    "width",
    "height",
    "min-width",
    "max-width",
    "min-height",
    "max-height",
    "margin",
    "margin-top",
    "margin-right",
    "margin-bottom",
    "margin-left",
    "padding",
    "padding-top",
    "padding-right",
    "padding-bottom",
    "padding-left",
    "box-sizing",

    // Flexbox & grid
    "flex",
    "flex-grow",
    "flex-shrink",
    "flex-basis",
    "justify-content",
    "align-items",
    "align-content",
    "align-self",
    "gap",
    "row-gap",
    "column-gap",
    "grid-template-columns",
    "grid-template-rows",
    "grid-column",
    "grid-row",

    // Typography
    "font",
    "font-family",
    "font-size",
    "font-weight",
    "font-style",
    "letter-spacing",
    "line-height",
    "text-align",
    "text-decoration",
    "text-transform",
    "white-space",
    "word-break",
    "color",

    // Background
    "background",
    "background-color",
    "background-image",
    "background-repeat",
    "background-size",
    "background-position",

    // Border
    "border",
    "border-width",
    "border-style",
    "border-color",
    "border-radius",
    "border-top",
    "border-right",
    "border-bottom",
    "border-left",

    // Shadow
    "box-shadow",
    "text-shadow",

    // Others (safe)
    "cursor",
    "opacity",
    "visibility",
    "vertical-align",
    "object-fit",
    "transition",
    "transform",
];

// utils/sanitizeCssClass.ts

const ALLOWED_CLASSNAME_PATTERN = /^[a-zA-Z0-9-_ ]+$/;

/**
 * Sanitize an inline style string by removing any properties
 * that are not in the allowed list.
 *
 * Example:
 *  Input: "width:100%;background-image:url('javascript:alert(1)');"
 *  Output: "width:100%;"
 *
 * @param style - The raw style string (e.g. from a database or user input)
 * @returns A safe inline style string with only allowed properties
 */
export function sanitizeInlineStyle(style: string): string {
    if (!style) return "";

    const filtered = style
        .split(";")
        .map((prop) => prop.trim())
        .filter((prop) => {
            const [key] = prop.split(":").map((s) => s.trim().toLowerCase());
            return ALLOWED_STYLE_PROPERTIES.includes(key);
        })
        .join("; ");

    return filtered ? `${filtered};` : "";
}

/**
 * Escapes HTML special characters to prevent XSS attacks
 * when injecting user-generated content into HTML strings.
 *
 * @param value - The string to sanitize
 * @returns The sanitized string with HTML entities
 */
export function sanitize(value: string): string {
    return value?.replace(/[&<>"]/g, (match) => {
        const map: Record<string, string> = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
        };
        return map[match] || match;
    });
}

/**
 * Sanitizes a CSS class name by allowing only alphanumeric characters,
 * hyphens, underscores, and spaces.
 *
 * @param className - The raw class name string
 * @returns A sanitized class name or an empty string if invalid
 */
export function sanitizeCssClass(className: string): string {
    if (!className) return "";
    return ALLOWED_CLASSNAME_PATTERN.test(className) ? className : "";
}
